'use client'

import { useLocale } from '@/components/Link'
import getDomainLangPaths from '@/helpers/getDomainLangPaths'
import { reportAuthError } from '@/helpers/reportAuthError'
import { Alert, Button, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'

import { Auth } from 'aws-amplify'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useSearchParams } from 'next/navigation'
import { ChangeEvent, useEffect, useState } from 'react'
import { z } from 'zod'

const emailValidation = z.string().email()

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: getDomainLangPaths(),
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = () => {
  return { props: {} }
}

const ForgotPassword = () => {
  const theme = useTheme()
  const searchParams = useSearchParams()
  const locale = useLocale()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [submitted, setSubmitted] = useState(false)
  const [disableResend, setDisableResend] = useState(false)

  useEffect(() => {
    const email = searchParams?.get('email')
    if (email) setEmail(email)
  }, [searchParams])

  const temporarilyDisableResend = () => {
    setDisableResend(true)
    setTimeout(() => {
      setDisableResend(false)
    }, 30000)
  }

  const handleSubmit = () => {
    const result = emailValidation.safeParse(email)
    if (!result.success) {
      setErrors(result.error.format()._errors)
      return
    }
    setSubmitted(true)
    temporarilyDisableResend()
    Auth.forgotPassword(email, { locale: locale || '' })
      .then(() => setSubmitted(true))
      .catch((error: Record<string, string>) => {
        reportAuthError('FORGOT_PASSWORD_REQUEST', error)
        let message = String(error)
        if (typeof error?.message === 'string') message = error?.message
        setErrors([message])
      })
  }

  const initialSubmitSuccess = submitted

  return (
    <form
      onSubmit={(e: any) => {
        e.preventDefault()
        handleSubmit()
      }}>
      <Stack spacing={2} px={isSmall ? 4 : 12}>
        <Typography variant="h3" textAlign={isMobile ? 'center' : 'start'}>
          {initialSubmitSuccess ? 'Verify your email' : 'Hey, welcome back!'}
        </Typography>
        <>
          {initialSubmitSuccess ? (
            <Typography fontSize={'20px'}>
              You will receive a link to reset your password via email. Check your inbox and click
              it to continue. It may take a few minutes to arrive.
            </Typography>
          ) : (
            <TextField
              label="Confirm email"
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              variant="outlined"
              value={email}
              error={errors.length > 0}
              helperText={errors.join(', ')}
              onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => setEmail(value)}
              data-dd-privacy="allow"
              data-testid="reset-password-email-input"
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          )}

          {initialSubmitSuccess ? (
            <Stack alignItems="center" direction="row">
              <Typography variant="body1" textAlign="center">
                Email not arrived after 5 minutes?
              </Typography>
              <Button type="submit" disabled={disableResend} variant="text" size="large">
                Resend it
              </Button>
            </Stack>
          ) : (
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              color={initialSubmitSuccess ? 'primary' : 'error'}
              data-testid="reset-password-submit-button">
              Send Reset Email
            </Button>
          )}
          {initialSubmitSuccess && errors.length > 0 ? (
            <Alert icon={false} severity="error">
              {errors.join(', ')}
            </Alert>
          ) : null}
        </>
      </Stack>
    </form>
  )
}

export default ForgotPassword
